import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { NS } from '../../../../i18n/constants/keys';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import { ISystemSettingsCardProps } from '../interfaces';

export const SystemSettingsCard: React.FC<ISystemSettingsCardProps> = ({ item }) => {
  const { t } = useTranslation(NS.navbarMenus);

  const cardContent = (
    <Paper
      component={Link}
      to={item.path}
      elevation={0}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'background.paper',
        textDecoration: 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: 180,
        '&:hover': {
          borderColor: item.badgeColor || 'primary.main',
          transform: 'translateY(-3px)',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)',
          '& .card-arrow': {
            transform: 'translateX(4px)',
            color: item.badgeColor || 'primary.main',
          },
          '& .card-icon': {
            transform: 'scale(1.06)',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box
          className="card-icon"
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2.5,
            background: item.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}
        >
          {item.icon}
        </Box>

        <Box
          className="card-arrow"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            transition: 'all 0.2s ease',
            pt: 0.5,
          }}
        >
          <ChevronRight size={20} />
        </Box>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.3,
            mb: 0.75,
          }}
        >
          {t(item.titleKey)}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.8125rem',
            lineHeight: 1.5,
          }}
        >
          {t(item.descriptionKey)}
        </Typography>
      </Box>

      <Box
        sx={{
          mt: 2.5,
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: item.badgeColor || 'primary.main',
            fontSize: '0.75rem',
          }}
        >
          Configure &rarr;
        </Typography>
      </Box>
    </Paper>
  );

  if (item.permissions && item.permissions.length > 0) {
    return (
      <PermissionGate
        requiredPermissions={item.permissions}
        matchAnyPermission
        requiredFeature={item.requiredFeature}
      >
        {cardContent}
      </PermissionGate>
    );
  }

  return cardContent;
};
